/**
 * @module RecruitingEinstellungenUnified
 * @description Unified Recruiting Settings — Pipeline, E-Mail & SMS, Karriere, Allgemein.
 *   Combines settings from both Fest-Recruiting and Flex-Pool modules.
 * @route /recruiting/einstellungen
 * @since v2.246.0
 * @updated v2.248.0 — Removed Templates tab, human-readable labels, IMAP config, Karriere links
 */
import { useState, useEffect } from 'react';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { ToggleSwitch } from '../components/ui';

const API = import.meta.env.VITE_API_URL || '';
const f = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });
const fj = (url, body, method = 'POST') => f(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

export default function RecruitingEinstellungenUnified() {
  const { t } = useLanguage();
  // #959 Bundle A: Pipeline-Tab entfernt (Status = Single Source of Truth);
  // Auto-Email-Templates werden in Bundle B als Status-Trigger neu aufgebaut.
  const [activeTab, setActiveTab] = useTabParam('email');

  const tabs = [
    { key: 'email', label: t('recruiting.settings.emailSms', 'E-Mail & SMS') },
    { key: 'karriere', label: t('recruiting.settings.karriere', 'Karriere-Seite') },
    { key: 'allgemein', label: t('recruiting.settings.allgemein', 'Allgemein') },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
          {t('recruiting.settings.title', 'Recruiting Einstellungen')}
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
          {t('recruiting.settings.subtitle', 'Pipeline, Kommunikation und allgemeine Einstellungen')}
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] w-fit">
        {tabs.map(tb => (
          <button key={tb.key} onClick={() => setActiveTab(tb.key)}
            className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
              activeTab === tb.key ? 'bg-white shadow-sm font-semibold dark:bg-[var(--bg-card)]' : ''
            }`}
            style={{ color: activeTab === tb.key ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
            {tb.label}
          </button>
        ))}
      </div>

      {activeTab === 'email' && <EmailSmsTab t={t} />}
      {activeTab === 'karriere' && <KarriereTab t={t} />}
      {activeTab === 'allgemein' && <AllgemeinTab t={t} />}
    </div>
  );
}

// ─── E-Mail & SMS Tab ───
function EmailSmsTab({ t }) {
  const [smtpConfig, setSmtpConfig] = useState({ host: '', port: '465', user: '', password: '', secure: 'ssl', from_name: '' });
  const [imapConfig, setImapConfig] = useState({ host: '', port: '993', user: '', password: '' });
  const [smsConfig, setSmsConfig] = useState({ account_sid: '', auth_token: '', from_number: '', enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // 'smtp' | 'imap' | null
  const [smtpResult, setSmtpResult] = useState(null);
  const [imapResult, setImapResult] = useState(null);
  const [smsResult, setSmsResult] = useState(null);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [imapTesting, setImapTesting] = useState(false);
  const [smsTesting, setSmsTesting] = useState(false);
  const [smsTestNr, setSmsTestNr] = useState('');
  const [imapPolling, setImapPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);
  const [imapIntervall, setImapIntervall] = useState('5');
  const [savingIntervall, setSavingIntervall] = useState(false);

  useEffect(() => {
    Promise.all([
      f('/api/recruiting/smtp-config').then(r => r.ok ? r.json() : {}),
      f('/api/recruiting/config').then(r => r.ok ? r.json() : {}),
      f('/api/recruiting/sms-config').then(r => r.ok ? r.json() : {}),
    ]).then(([smtp, rc, sms]) => {
      setSmtpConfig({
        host: smtp.recruiting_smtp_host || '',
        port: smtp.recruiting_smtp_port || '587',
        user: smtp.recruiting_smtp_user || '',
        password: smtp.recruiting_smtp_pass || '',
        secure: smtp.recruiting_smtp_secure || 'starttls',
        from_name: smtp.recruiting_smtp_from_name || '',
      });
      setImapConfig({
        host: rc.recruiting_imap_host || '',
        port: rc.recruiting_imap_port || '993',
        user: rc.recruiting_imap_user || '',
        password: rc.recruiting_imap_pass || '',
      });
      if (rc.recruiting_imap_intervall !== undefined) setImapIntervall(String(rc.recruiting_imap_intervall));
      setSmsConfig({
        account_sid: sms.account_sid || '',
        auth_token: sms.auth_token || '',
        from_number: sms.from_number || '',
        enabled: !!sms.enabled,
      });
    }).finally(() => setLoading(false));
  }, []);

  const saveSMTP = async () => {
    setSaving('smtp');
    setSmtpResult(null);
    try {
      const res = await fj('/api/recruiting/smtp-config', {
        recruiting_smtp_host: smtpConfig.host,
        recruiting_smtp_port: smtpConfig.port,
        recruiting_smtp_user: smtpConfig.user,
        recruiting_smtp_pass: smtpConfig.password,
        recruiting_smtp_secure: smtpConfig.secure,
        recruiting_smtp_from_name: smtpConfig.from_name,
      }, 'PUT');
      if (res.ok) {
        setSmtpResult({ success: true, message: t('common.saved', 'Gespeichert') });
        setTimeout(() => setSmtpResult(null), 3000);
      } else {
        setSmtpResult({ success: false, message: 'Speichern fehlgeschlagen' });
        setTimeout(() => setSmtpResult(null), 5000);
      }
    } catch { setSmtpResult({ success: false, message: 'Netzwerkfehler' }); }
    setSaving(null);
  };

  const saveIMAP = async () => {
    setSaving('imap');
    setImapResult(null);
    try {
      const res = await fj('/api/recruiting/config', {
        recruiting_imap_host: imapConfig.host,
        recruiting_imap_port: imapConfig.port,
        recruiting_imap_user: imapConfig.user,
        recruiting_imap_pass: imapConfig.password,
      }, 'PUT');
      if (res.ok) {
        setImapResult({ success: true, message: t('common.saved', 'Gespeichert') });
        setTimeout(() => setImapResult(null), 3000);
      } else {
        setImapResult({ success: false, message: 'Speichern fehlgeschlagen' });
        setTimeout(() => setImapResult(null), 5000);
      }
    } catch { setImapResult({ success: false, message: 'Netzwerkfehler' }); }
    setSaving(null);
  };

  const testSMTP = async () => {
    setSmtpTesting(true);
    setSmtpResult(null);
    try {
      const res = await fj('/api/recruiting/test-smtp', {});
      const data = await res.json().catch(() => ({}));
      setSmtpResult(data);
    } catch { setSmtpResult({ success: false, message: 'Netzwerkfehler' }); }
    setSmtpTesting(false);
    setTimeout(() => setSmtpResult(null), 8000);
  };

  const testIMAP = async () => {
    setImapTesting(true);
    setImapResult(null);
    try {
      const res = await fj('/api/recruiting/test-imap', {});
      const data = await res.json().catch(() => ({}));
      setImapResult(data);
    } catch { setImapResult({ success: false, message: 'Netzwerkfehler' }); }
    setImapTesting(false);
    setTimeout(() => setImapResult(null), 8000);
  };

  const handleImapPoll = async () => {
    setImapPolling(true);
    setPollResult(null);
    try {
      const res = await fj('/api/recruiting/imap-poll', {});
      const data = await res.json().catch(() => ({}));
      setPollResult(data);
    } catch { setPollResult({ success: false, message: 'Netzwerkfehler' }); }
    setImapPolling(false);
    setTimeout(() => setPollResult(null), 10000);
  };

  const saveIntervall = async (val) => {
    setImapIntervall(val);
    setSavingIntervall(true);
    try {
      await fj('/api/recruiting/config', { recruiting_imap_intervall: val }, 'PUT');
    } catch { /* ignore */ }
    setSavingIntervall(false);
  };

  const testSMS = async () => {
    if (!smsTestNr.trim()) return;
    setSmsTesting(true);
    setSmsResult(null);
    try {
      const res = await fj('/api/recruiting/test-sms', { to: smsTestNr.trim() });
      const data = await res.json().catch(() => ({}));
      setSmsResult(data);
    } catch { setSmsResult({ success: false, message: 'Netzwerkfehler' }); }
    setSmsTesting(false);
    setTimeout(() => setSmsResult(null), 8000);
  };

  if (loading) return <div className="p-4 text-sm" style={{ color: 'var(--text-muted)' }}>{t('common.loading', 'Laden...')}</div>;

  const SMTP_FIELDS = [
    { key: 'host', label: t('recruiting.settings.smtpServer', 'SMTP Server'), type: 'text' },
    { key: 'port', label: t('recruiting.settings.port', 'Port'), type: 'text' },
    { key: 'user', label: t('recruiting.settings.benutzername', 'Benutzername'), type: 'text' },
    { key: 'password', label: t('recruiting.settings.passwort', 'Passwort'), type: 'password' },
    { key: 'from_name', label: t('recruiting.settings.absendername', 'Absendername'), type: 'text' },
  ];

  const IMAP_FIELDS = [
    { key: 'host', label: t('recruiting.settings.imapHost', 'IMAP Server'), type: 'text' },
    { key: 'port', label: t('recruiting.settings.imapPort', 'IMAP Port'), type: 'text' },
    { key: 'user', label: t('recruiting.settings.imapUser', 'IMAP Benutzername'), type: 'text' },
    { key: 'password', label: t('recruiting.settings.imapPasswort', 'IMAP Passwort'), type: 'password' },
  ];

  const SMS_FIELDS = [
    { key: 'account_sid', label: 'Twilio Account SID', type: 'text' },
    { key: 'auth_token', label: 'Auth Token', type: 'password' },
    { key: 'from_number', label: t('recruiting.settings.absenderNummer', 'Absender-Nummer'), type: 'text' },
  ];

  const SSL_OPTIONS = [
    { value: 'starttls', label: 'STARTTLS (Port 587)' },
    { value: 'ssl', label: 'SSL/TLS (Port 465)' },
    { value: 'none', label: t('recruiting.settings.sslNone', 'None (Port 25)') },
  ];

  const ResultBadge = ({ result }) => result ? (
    <span className="text-xs font-medium" style={{ color: result.success ? '#10B981' : '#EF4444' }}>
      {result.message}
    </span>
  ) : null;

  return (
    <div className="space-y-6">
      {/* SMTP Config */}
      <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card)' }}>
        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>
          {t('recruiting.settings.smtpConfig', 'SMTP Konfiguration (Ausgangsserver)')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SMTP_FIELDS.map(field => (
            <div key={field.key}>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-tertiary)' }}>{field.label}</label>
              <input
                type={field.type}
                value={smtpConfig[field.key] || ''}
                onChange={e => setSmtpConfig(p => ({ ...p, [field.key]: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }}
                autoComplete="off"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-tertiary)' }}>
              {t('recruiting.settings.sslTls', 'Verschlüsselung')}
            </label>
            <select
              value={smtpConfig.secure || 'starttls'}
              onChange={e => setSmtpConfig(p => ({ ...p, secure: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border text-sm bg-[var(--bg-card,#fff)]" style={{ borderColor: '#E5E7EB' }}
            >
              {SSL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <button onClick={saveSMTP} disabled={saving === 'smtp'} className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60" style={{ background: '#338edc' }}>
            {saving === 'smtp' ? '...' : t('common.save', 'Speichern')}
          </button>
          <button onClick={testSMTP} disabled={smtpTesting} className="px-4 py-2 rounded-lg border text-sm font-medium disabled:opacity-60" style={{ borderColor: 'var(--border-card)', color: 'var(--text-secondary)' }}>
            {smtpTesting ? '...' : t('recruiting.settings.testEmail', 'Test-E-Mail senden')}
          </button>
          <ResultBadge result={smtpResult} />
        </div>
      </div>

      {/* IMAP Config */}
      <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card)' }}>
        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>
          {t('recruiting.settings.imapConfig', 'IMAP Konfiguration (Posteingang)')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {IMAP_FIELDS.map(field => (
            <div key={field.key}>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-tertiary)' }}>{field.label}</label>
              <input
                type={field.type}
                value={imapConfig[field.key] || ''}
                onChange={e => setImapConfig(p => ({ ...p, [field.key]: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB' }}
                autoComplete="off"
              />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <button onClick={saveIMAP} disabled={saving === 'imap'} className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60" style={{ background: '#338edc' }}>
            {saving === 'imap' ? '...' : t('common.save', 'Speichern')}
          </button>
          <button onClick={testIMAP} disabled={imapTesting} className="px-4 py-2 rounded-lg border text-sm font-medium disabled:opacity-60" style={{ borderColor: 'var(--border-card)', color: 'var(--text-secondary)' }}>
            {imapTesting ? '...' : t('recruiting.settings.testImap', 'Verbindung testen')}
          </button>
          <button onClick={handleImapPoll} disabled={imapPolling} className="px-4 py-2 rounded-lg border text-sm font-medium disabled:opacity-60 inline-flex items-center gap-1.5" style={{ borderColor: 'var(--border-card)', color: 'var(--text-secondary)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={imapPolling ? 'animate-spin' : ''}>
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" />
            </svg>
            {imapPolling ? t('recruiting.settings.polling', 'Abrufen...') : t('recruiting.settings.pollNow', 'Jetzt abrufen')}
          </button>
          <ResultBadge result={imapResult} />
        </div>
        {pollResult && (
          <div className={`mt-3 p-3 rounded-lg border text-sm ${pollResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            {pollResult.success ? (
              <span style={{ color: 'var(--text-secondary)' }}>
                <strong>{pollResult.neue_emails}</strong> {t('recruiting.settings.pollNewEmails', 'neue E-Mails')} — <strong>{pollResult.zugeordnet}</strong> {t('recruiting.settings.pollMatched', 'zugeordnet')}, <strong>{pollResult.nicht_zugeordnet}</strong> {t('recruiting.settings.pollUnmatched', 'nicht zugeordnet')}
                {pollResult.anhaenge > 0 && <>, <strong>{pollResult.anhaenge}</strong> {t('recruiting.settings.pollAttachments', 'Anhänge')}</>}
              </span>
            ) : (
              <span style={{ color: '#EF4444' }}>{pollResult.message}</span>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-card)' }}>
          <label className="text-xs font-medium whitespace-nowrap" style={{ color: 'var(--text-tertiary)' }}>
            {t('recruiting.settings.pollInterval', 'Abruf-Intervall')}
          </label>
          <select
            value={imapIntervall}
            onChange={e => saveIntervall(e.target.value)}
            disabled={savingIntervall}
            className="px-3 py-1.5 rounded-lg border text-sm bg-[var(--bg-card,#fff)]" style={{ borderColor: '#E5E7EB' }}
          >
            <option value="0">{t('recruiting.settings.pollDisabled', 'Deaktiviert')}</option>
            <option value="1">1 {t('common.minutes', 'Minuten')}</option>
            <option value="2">2 {t('common.minutes', 'Minuten')}</option>
            <option value="5">5 {t('common.minutes', 'Minuten')}</option>
            <option value="10">10 {t('common.minutes', 'Minuten')}</option>
            <option value="15">15 {t('common.minutes', 'Minuten')}</option>
            <option value="30">30 {t('common.minutes', 'Minuten')}</option>
          </select>
          {savingIntervall && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>...</span>}
        </div>
      </div>

      {/* SMS Config */}
      <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card)' }}>
        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>
          {t('recruiting.settings.smsConfig', 'SMS Konfiguration (Twilio)')}
        </h3>
        <p className="text-xs mb-4" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
          {t('recruiting.settings.smsInfo', 'SMS is sent via the central Twilio configuration.')}
          {smsConfig.enabled && <span className="ml-2 text-[#10B981] font-medium">Aktiv</span>}
          {!smsConfig.enabled && <span className="ml-2 text-[#EF4444] font-medium">Inaktiv</span>}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SMS_FIELDS.map(field => (
            <div key={field.key}>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-tertiary)' }}>{field.label}</label>
              <input
                type={field.type}
                value={smsConfig[field.key] || ''}
                readOnly
                className="w-full px-3 py-2 rounded-lg border text-sm bg-[var(--bg-inset)]" style={{ borderColor: '#E5E7EB' }}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <input
            type="text"
            value={smsTestNr}
            onChange={e => setSmsTestNr(e.target.value)}
            placeholder={t('recruiting.settings.smsTestPlaceholder', '+43...')}
            className="px-3 py-2 rounded-lg border text-sm w-48" style={{ borderColor: '#E5E7EB' }}
          />
          <button onClick={testSMS} disabled={smsTesting || !smsTestNr.trim()} className="px-4 py-2 rounded-lg border text-sm font-medium disabled:opacity-60" style={{ borderColor: 'var(--border-card)', color: 'var(--text-secondary)' }}>
            {smsTesting ? '...' : t('recruiting.settings.testSms', 'Test-SMS senden')}
          </button>
          <ResultBadge result={smsResult} />
        </div>
      </div>
    </div>
  );
}

// ─── Karriere Tab ───
function KarriereTab({ t }) {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const appUrl = window.location.origin;

  useEffect(() => {
    f('/api/recruiting/config').then(r => r.ok ? r.json() : {}).then(setConfig).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    await fj('/api/recruiting/config', config, 'PUT');
    setSaving(false);
  };

  if (loading) return <div className="p-4 text-sm" style={{ color: 'var(--text-muted)' }}>{t('common.loading', 'Laden...')}</div>;

  const setVal = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  return (
    <div className="space-y-6">
      {/* Quick Links */}
      <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card)' }}>
        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>
          {t('recruiting.settings.karriereLinks', 'Karriere-Seiten')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--border-card)' }}>
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              {t('recruiting.settings.karriereFest', 'Karriere-Seite (Festanstellung)')}
            </p>
            <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>{appUrl}/karriere</p>
            <a href="/karriere" target="_blank" rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5"
              style={{ borderColor: 'var(--border-card)', color: '#338edc' }}>
              {t('recruiting.settings.oeffnen', 'Öffnen')} ↗
            </a>
          </div>
          <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--border-card)' }}>
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              {t('recruiting.settings.karriereFlex', 'Flex Landing-Page')}
            </p>
            <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>{appUrl}/jobs</p>
            <a href="/jobs" target="_blank" rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5"
              style={{ borderColor: 'var(--border-card)', color: '#338edc' }}>
              {t('recruiting.settings.oeffnen', 'Öffnen')} ↗
            </a>
          </div>
        </div>
      </div>

      {/* Fest Karriere Config */}
      <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card)' }}>
        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>
          {t('recruiting.settings.karriereConfig', 'Karriere-Seite Konfiguration')}
        </h3>
        <div className="space-y-3">
          <CfgInput label={t('recruiting.settings.heroTitel', 'Hero-Titel')} value={config.karriere_hero_titel || ''} onChange={v => setVal('karriere_hero_titel', v)} />
          <CfgInput label={t('recruiting.settings.heroText', 'Hero-Text')} value={config.karriere_hero_text || ''} onChange={v => setVal('karriere_hero_text', v)} textarea />
          <CfgInput label={t('recruiting.settings.youtubeId', 'YouTube Video-ID')} value={config.karriere_youtube_video_id || ''} onChange={v => setVal('karriere_youtube_video_id', v)} />
          <CfgInput label={t('recruiting.settings.googleMaps', 'Google Maps Embed-URL')} value={config.karriere_google_maps_embed || ''} onChange={v => setVal('karriere_google_maps_embed', v)} />
        </div>

        {/* #1779 — Bewerbungsformular */}
        <div className="mt-5 pt-5 border-t space-y-1" style={{ borderColor: 'var(--border-card)' }}>
          <CfgToggle label={t('recruiting.settings.lebenslaufPflicht', 'Lebenslauf im Bewerbungsformular verpflichtend')}
            value={config.karriere_lebenslauf_pflicht !== false && config.karriere_lebenslauf_pflicht !== 'false'}
            onChange={v => setVal('karriere_lebenslauf_pflicht', v)} />
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {t('recruiting.settings.lebenslaufPflichtHinweis', 'Aktiv: Bewerbungen ohne Lebenslauf werden serverseitig abgelehnt. Inaktiv: Lebenslauf ist optional.')}
          </p>
        </div>

        <button onClick={save} disabled={saving} className="mt-4 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60" style={{ background: '#338edc' }}>
          {saving ? '...' : t('common.save', 'Speichern')}
        </button>
      </div>
    </div>
  );
}

// ─── Allgemein Tab ───
function AllgemeinTab({ t }) {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    Promise.all([
      f('/api/recruiting/config').then(r => r.ok ? r.json() : {}),
      f('/api/recruiting/hr-users').then(r => r.ok ? r.json() : []),
    ]).then(([cfg, u]) => {
      setConfig(cfg);
      setUsers(Array.isArray(u) ? u : []);
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    await fj('/api/recruiting/config', config, 'PUT');
    setSaving(false);
  };

  if (loading) return <div className="p-4 text-sm" style={{ color: 'var(--text-muted)' }}>{t('common.loading', 'Laden...')}</div>;

  const setVal = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));
  const hrIds = (() => {
    const v = config.recruiting_hr_notifications;
    if (Array.isArray(v)) return v;
    if (typeof v === 'string') { try { return JSON.parse(v); } catch { return []; } }
    return [];
  })();
  const toggleHrUser = (uid) => {
    const next = hrIds.includes(uid) ? hrIds.filter(x => x !== uid) : [...hrIds, uid];
    setVal('recruiting_hr_notifications', next);
  };

  return (
    <div className="space-y-4">
      <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5 space-y-4" style={{ borderColor: 'var(--border-card)' }}>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
          {t('recruiting.settings.allgemeinKonfig', 'Allgemeine Einstellungen')}
        </h3>
        <CfgToggle label={t('recruiting.settings.autoEmail', 'Auto-E-Mail bei Pipeline-Wechsel')}
          value={config.recruiting_auto_email_aktiv !== false && config.recruiting_auto_email_aktiv !== 'false'}
          onChange={v => setVal('recruiting_auto_email_aktiv', v)} />
        <CfgToggle label={t('recruiting.settings.autoSms', 'Auto-SMS bei Pipeline-Wechsel')}
          value={config.recruiting_auto_sms_aktiv !== false && config.recruiting_auto_sms_aktiv !== 'false'}
          onChange={v => setVal('recruiting_auto_sms_aktiv', v)} />
        <CfgToggle label={t('recruiting.settings.eingangsbestaetigung', 'Eingangsbestätigung per E-Mail')}
          value={config.recruiting_eingangsbestaetigung_aktiv !== false && config.recruiting_eingangsbestaetigung_aktiv !== 'false'}
          onChange={v => setVal('recruiting_eingangsbestaetigung_aktiv', v)} />
        <div>
          <label className="block text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
            {t('recruiting.settings.duplikatCheck', 'Duplikat-Erkennung')}
          </label>
          <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] w-fit">
            {[
              { key: 'warn', label: t('recruiting.settings.duplikatWarn', 'Warnung') },
              { key: 'block', label: t('recruiting.settings.duplikatBlock', 'Blockieren') },
              { key: 'off', label: t('recruiting.settings.duplikatOff', 'Aus') },
            ].map(opt => (
              <button key={opt.key} type="button" onClick={() => setVal('recruiting_dupe_check_mode', opt.key)}
                className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
                  (config.recruiting_dupe_check_mode || 'warn') === opt.key ? 'bg-white shadow-sm font-semibold dark:bg-[var(--bg-card)]' : ''
                }`}
                style={{ color: (config.recruiting_dupe_check_mode || 'warn') === opt.key ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
            {t('recruiting.settings.duplikatHinweis', 'Prüft ob E-Mail oder Name bereits als Bewerber existiert')}
          </p>
        </div>
        <CfgInput label={t('recruiting.settings.dsgvoTage', 'DSGVO Löschfrist (Tage)')} type="number"
          value={config.recruiting_dsgvo_loeschfrist_tage || '180'} onChange={v => setVal('recruiting_dsgvo_loeschfrist_tage', v)} />
        <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60" style={{ background: '#338edc' }}>
          {saving ? '...' : t('common.save', 'Speichern')}
        </button>
      </div>

      {/* HR-Benachrichtigungen */}
      <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card)' }}>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
          {t('recruiting.settings.hrNotifications', 'HR-Benachrichtigungen')}
        </h3>
        <p className="text-xs mt-1 mb-3" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
          {t('recruiting.settings.hrNotificationsHint', 'Diese Benutzer werden bei neuen Bewerbungen und Bewerber-Nachrichten benachrichtigt.')}
        </p>
        {users.length === 0 ? (
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('common.noData', 'Keine Daten')}</p>
        ) : (
          <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
            {users.map(u => (
              <label key={u.id} className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg cursor-pointer hover:bg-[var(--bg-inset,#F8F9FC)]">
                <input type="checkbox" checked={hrIds.includes(u.id)} onChange={() => toggleHrUser(u.id)}
                  className="w-4 h-4 rounded border-[var(--border-default,#E5E7EB)] accent-[#338edc]" />
                <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{u.vorname} {u.nachname}</span>
                <span className="text-xs" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{u.rolle}</span>
              </label>
            ))}
          </div>
        )}
        <button onClick={save} disabled={saving} className="mt-3 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60" style={{ background: '#338edc' }}>
          {saving ? '...' : t('common.save', 'Speichern')}
        </button>
      </div>

      <FlexAbsageCard t={t} />
    </div>
  );
}

// ─── #1819 Einsatz-Absagen von Flexworkern ───
// Eigener Lade-/Speicherweg: die Schlüssel liegen in system_config (category 'flex')
// und werden von /api/flex/settings bedient, nicht von /api/recruiting/config.
function FlexAbsageCard({ t }) {
  const [enabled, setEnabled] = useState(false);
  const [ids, setIds] = useState([]);
  const [kanaele, setKanaele] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gespeichert, setGespeichert] = useState(false);

  useEffect(() => {
    Promise.all([
      f('/api/flex/settings').then(r => r.ok ? r.json() : null).catch(() => null),
      f('/api/flex/absage/empfaenger-status').then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([cfg, status]) => {
      const s = cfg?.settings || {};
      setEnabled(String(s.flex_absage_enabled?.value ?? 'false').replace(/"/g, '') === 'true');
      setIds(parseIds(s.flex_absage_recipients?.value));
      if (Array.isArray(status?.users)) setKanaele(status.users);
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    const res = await fj('/api/flex/settings', { flex_absage_enabled: enabled, flex_absage_recipients: ids }, 'PUT');
    setSaving(false);
    setGespeichert(res.ok);
    if (res.ok) setTimeout(() => setGespeichert(false), 3000);
  };

  const toggleUser = (uid) => setIds(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);

  if (loading) return null;

  return (
    <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card)' }}>
      <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
        {t('flex.settings.absageTitle', 'Einsatz-Absagen erkennen')}
      </h3>
      <p className="text-xs mt-1 mb-3" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
        {t('flex.settings.absageInfo', 'Eingehende E-Mails und SMS von Flexworkern mit bevorstehendem Einsatz werden auf Absagen geprüft und gemeldet. Ein Einsatz wird nie automatisch storniert.')}
      </p>

      <div className="mb-4">
        <CfgToggle label={t('flex.settings.absageEnabled', 'Absage-Erkennung aktiv')} value={enabled} onChange={setEnabled} />
        <p className="text-xs mt-1" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
          {t('flex.settings.absageEnabledHint', 'Ist der Schalter aus, wird keine Nachricht geprüft.')}
        </p>
      </div>

      <label className="block text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
        {t('flex.settings.absageRecipients', 'Empfänger der Meldung')}
      </label>
      {kanaele.length === 0 ? (
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('flex.settings.noUsers', 'Keine Benutzer')}</p>
      ) : (
        <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
          {kanaele.map(u => (
            <label key={u.id} className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg cursor-pointer hover:bg-[var(--bg-inset,#F8F9FC)]">
              <input type="checkbox" checked={ids.includes(u.id)} onChange={() => toggleUser(u.id)}
                className="w-4 h-4 rounded border-[var(--border-default,#E5E7EB)] accent-[#338edc]" />
              <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{u.name}</span>
              <span className="ml-auto"><KanalBadge kanal={u.kanal} t={t} /></span>
            </label>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 mt-3">
        <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60" style={{ background: '#338edc' }}>
          {saving ? '...' : t('common.save', 'Speichern')}
        </button>
        {gespeichert && <span className="text-xs" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('common.saved', 'Gespeichert')}</span>}
      </div>
    </div>
  );
}

// PUT /api/flex/settings hat historisch bereits stringifizierte Arrays geschrieben → doppelt kodiert.
function parseIds(v) {
  let arr = v;
  for (let i = 0; i < 2 && typeof arr === 'string'; i++) {
    try { arr = JSON.parse(arr); } catch { return []; }
  }
  return Array.isArray(arr) ? arr.map(x => parseInt(x, 10)).filter(Number.isInteger) : [];
}

function KanalBadge({ kanal, t }) {
  const map = {
    telegram: ['Telegram', 'bg-blue-50 text-blue-700 border-blue-200'],
    sms: ['SMS', 'bg-amber-50 text-amber-700 border-amber-200'],
    kein_kanal: [t('flex.settings.absageNoChannel', 'kein Kanal'), 'bg-red-50 text-red-700 border-red-200'],
  };
  const [label, cls] = map[kanal] || map.kein_kanal;
  return <span className={`px-1.5 py-0.5 rounded-md border text-[10px] font-medium ${cls}`}>{label}</span>;
}

// ─── Shared UI ───
function CfgToggle({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{label}</span>
      <ToggleSwitch checked={value} onChange={onChange} />
    </div>
  );
}

function CfgInput({ label, value, onChange, textarea, type = 'text' }) {
  const Tag = textarea ? 'textarea' : 'input';
  return (
    <div>
      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-tertiary)' }}>{label}</label>
      <Tag value={value} onChange={e => onChange(e.target.value)} type={textarea ? undefined : type}
        rows={textarea ? 3 : undefined}
        className={`w-full px-3 py-2 rounded-lg border text-sm ${textarea ? 'resize-none' : ''}`}
        style={{ borderColor: 'var(--border-input, #E5E7EB)', color: 'var(--text-primary)' }} />
    </div>
  );
}
